import {
  Button,
  Form,
  FormLayout,
  Modal,
  ModalBody,
  ModalFooter,
  TextArea,
  TextField,
  useModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { useRef } from 'react';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import { useUpdateCollection } from '@/services/db/collection/mutations';
import type { Collection } from '@/services/db/collection/entity';
import { buildDescriptionRequest, buildRenameRequest } from './collection-payload';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name'),
  description: z.string(),
});

const FORM_ID = 'edit-collection-details-form';

export type CollectionDetailsModalData = {
  collection: Collection;
  /** Threaded in because managed modals render above the router/AuthProvider. */
  workspaceId: number;
  onSaved?: () => void;
};

export function CollectionDetailsModal({
  modalState,
  data,
  onClose,
}: PlumaManagedModalProps<CollectionDetailsModalData>) {
  const { collection, workspaceId, onSaved } = data;
  const update = useUpdateCollection({ workspaceId });
  const snackbars = useSnackbars();

  const form = useZodForm({
    schema,
    defaultValues: { name: collection.name, description: collection.description ?? '' },
    mode: 'onChange',
  });

  // Tracks what's actually persisted, advanced after each successful update. Change
  // detection reads this (not the original prop) so a retry after a partial failure
  // re-sends only the field that hasn't landed — never the one already saved.
  const saved = useRef({ name: collection.name, description: collection.description ?? '' });

  const handleSubmit = form.handleSubmit(async (values) => {
    const name = values.name.trim();
    const nameChanged = name !== saved.current.name;
    const descriptionChanged = values.description !== saved.current.description;

    // A whitespace-only edit trims back to the current value — nothing to persist,
    // so close without signalling a save.
    if (!nameChanged && !descriptionChanged) {
      onClose();
      return;
    }

    try {
      if (nameChanged) {
        await update.mutateAsync({ id: collection.id, update: buildRenameRequest(name) });
        saved.current.name = name;
      }
      if (descriptionChanged) {
        await update.mutateAsync({ id: collection.id, update: buildDescriptionRequest(values.description) });
        saved.current.description = values.description;
      }
      onSaved?.();
      onClose();
    } catch {
      snackbars.create({ message: "Collection couldn't be updated. Try again.", variant: 'error' });
    }
  });

  return (
    <Modal state={modalState} title="Edit collection details" onClose={onClose}>
      <ModalBody>
        <Form id={FORM_ID} onSubmit={handleSubmit}>
          <FormLayout>
            <TextField {...form.registerPluma('name')} label="Name" autofocus={true} autoComplete="off" />
            <TextArea {...form.registerPluma('description')} label="Description" />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          isLoading={update.isPending}
          isDisabled={!form.formState.isValid || !form.formState.isDirty}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useCollectionDetailsModal() {
  return useModal(CollectionDetailsModal);
}
